import CardVisual from '@cohort/components-xps/components/cards/CardVisual';
import JourneyStatusBadge from '@cohort/components-xps/components/journeys/JourneyStatusBadge';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import JourneyStepsProgression from '@cohort/wallet/components/space/JourneyStepsProgression';
import useClickTracker from '@cohort/wallet/hooks/clickTracker';
import useInViewTracker from '@cohort/wallet/hooks/inViewTracker';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {getJourneyDetailRoute} from '@cohort/wallet/lib/Pages';
import type {CampaignJourneyWDto} from '@cohort/wallet-schemas/campaign';
import {CaretRight} from '@phosphor-icons/react';
import {Gift} from '@phosphor-icons/react';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

type JourneyCardProps = {
  campaign: CampaignJourneyWDto;
};

type RewardTagProps = JourneyCardProps;

const NewTag: React.FC = () => {
  const {accentColor, hasDarkAccent} = useThemeContext();
  const {t} = useTranslation('components', {keyPrefix: 'space.journeyCard'});

  return (
    <div
      className={classNames(
        'self-start rounded-md px-2 py-1 text-xs shadow-sm',
        hasDarkAccent ? 'text-white' : 'text-black'
      )}
      style={{
        backgroundColor: accentColor,
      }}
    >
      {t('newTag')}
    </div>
  );
};

const RewardTag: React.FC<RewardTagProps> = ({campaign}) => {
  const {hasDarkBg} = useThemeContext();
  const {t} = useTranslation('components', {keyPrefix: 'space.journeyCard'});

  function getRewardText(): string {
    if (campaign.perks.length === 0) {
      return t('digitalAssetReward');
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return campaign.perks[0]!.displayName;
  }

  return (
    <div className="self-start">
      <div
        className={classNames(
          'grid items-center gap-1 rounded-md px-2 py-1 text-xs shadow-sm [grid-template-columns:min-content_1fr_min-content]',
          hasDarkBg ? 'bg-white text-black' : 'bg-gray-300 text-gray-700'
        )}
      >
        <Gift className="inline-block h-4 w-4" />
        <div className="truncate">{getRewardText()}</div>
        {campaign.perks.length > 1 && <span>+{campaign.perks.length - 1}</span>}
      </div>
    </div>
  );
};

const JourneyCard: React.FC<JourneyCardProps> = ({campaign}) => {
  const {setTransitioning} = useSpaceContext();
  const {hasDarkBg, accentColor} = useThemeContext();
  const {isMobile} = useScreenSizeContext();
  const navigate = useNavigate();
  const trackingCallback = useClickTracker('journeys.list.card', {
    journeyId: campaign.id,
  });
  const ref = useInViewTracker('journeys.card', {
    journeyId: campaign.id,
  });

  const hideCover =
    isMobile && !campaign.journey.animationFileKey && !campaign.journey.imageFileKey;
  const hasVideoMedia = campaign.journey.animationFileKey !== null;
  const hasOptedIn = campaign.journey.participation !== null;
  const completedAt = campaign.journey.participation?.completedAt ?? null;

  function onCardClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    e.stopPropagation();
    trackingCallback();
    setTransitioning({active: true, enterFrom: 'left'});
    navigate(getJourneyDetailRoute(campaign.id));
  }

  return (
    <div
      ref={ref}
      className={classNames(
        'flex w-full cursor-pointer flex-row overflow-hidden rounded-xl shadow-sm',
        hasDarkBg ? 'bg-white/8' : 'bg-black/4'
      )}
      onClick={!hasVideoMedia ? onCardClick : undefined}
    >
      {!hideCover && (
        <div
          className={classNames(
            // Height = Width to keep a squared aspect ratio even without image.
            'w-[var(--journey-card-cover-width-mobile)] shrink-0 p-3 md:h-[var(--journey-card-cover-width)] md:w-[var(--journey-card-cover-width)] md:p-4'
          )}
        >
          <CardVisual
            bannerFileKey={campaign.journey.imageFileKey}
            thumbnailFileKey={null}
            videoFileKey={campaign.journey.animationFileKey}
            visualName={campaign.journey.title}
            autoPlayVideo
            layout="column"
          />
        </div>
      )}
      <div
        className="flex flex-grow flex-col justify-between space-y-3 rounded-b-xl p-3 md:p-4"
        onClick={onCardClick}
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <h3
                className={classNames(
                  'text-lg font-semibold',
                  hasDarkBg ? 'text-white' : 'text-black'
                )}
              >
                {campaign.journey.title}
              </h3>
              <CaretRight size={24} style={{color: accentColor}} />
            </div>
            <JourneyStatusBadge
              isCampaignEnded={campaign.status === 'ended'}
              completedAt={completedAt}
              hasDarkBg={hasDarkBg}
              availableSupply={campaign.availableSupply}
            />
          </div>
          <div className="w-fit">
            <RewardTag campaign={campaign} />
          </div>
        </div>
        <div className="flex flex-col space-y-3">
          {hasOptedIn ? <JourneyStepsProgression steps={campaign.journey.steps} /> : <NewTag />}
        </div>
      </div>
    </div>
  );
};

export default JourneyCard;
